import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useMutation } from '@/utils/api';

const error = {
  CREATE: 'Failed to create ticket',
  ASSOCIATE: 'Failed to associate ticket',
  DISCONNECT: 'Failed to disconnect ticket',
  ADD_COMMENT: 'Failed to add comment to Jira ticket',
};

export function useCreateTicket() {
  const axios = useAxios();

  return useMutation({
    defaultErrorMessage: 'Failed to create jira ticket',
    mutationFn: ({
      riskKey,
      account,
      templateId,
    }: {
      riskKey: string;
      account: string;
      templateId: string;
    }) => {
      const promise = axios.post('/ticket', {
        portal: 'jira',
        risk: riskKey,
        account,
        templateId,
      });

      toast.promise(promise, {
        loading: 'Creating Jira ticket...',
        success: 'New Jira ticket created',
        error: err => {
          const message = err.message.replace(`${error.CREATE}: `, '');

          return `${error.CREATE}${message ? `: ${message}` : ''}`;
        },
      });

      return promise;
    },
  });
}

export function useAssociateTicket() {
  const axios = useAxios();

  return useMutation({
    defaultErrorMessage: 'Failed to associate jira ticket',
    mutationFn: ({
      riskKey,
      ticketId,
      account,
    }: {
      riskKey: string;
      ticketId: string;
      account: string;
    }) => {
      const promise = axios.post('/ticket', {
        risk: riskKey,
        id: ticketId,
        account,
      });

      toast.promise(promise, {
        loading: 'Associating Jira ticket...',
        success: 'Vulnerability associated with Jira ticket',
        error: err => {
          const message = err.message.replace(`${error.ASSOCIATE}: `, '');

          return `${error.ASSOCIATE}${message ? `: ${err.message}` : ''}`;
        },
      });

      return promise;
    },
  });
}

export function useDisconnectTicket() {
  const axios = useAxios();

  return useMutation({
    defaultErrorMessage: 'Failed to disconnect jira ticket',
    mutationFn: ({ attributeKey }: { attributeKey: string }) => {
      const promise = axios.delete(`/ticket`, {
        data: { attribute: attributeKey },
      });

      toast.promise(promise, {
        loading: 'Disconnecting Jira ticket...',
        success: 'Jira ticket disconnected',
        error: 'Failed to disconnect Jira ticket',
      });

      return promise;
    },
  });
}

export function useAddJiraComment() {
  const axios = useAxios();

  return useMutation({
    defaultErrorMessage: 'Failed to add comment to Jira ticket',
    mutationFn: ({
      ticketId,
      comment,
      account,
    }: {
      ticketId: string;
      comment: string;
      account: string;
    }) => {
      const promise = axios.post('/ticket/comment', {
        ticketId,
        comment,
        account,
      });

      toast.promise(promise, {
        loading: 'Adding comment to Jira ticket...',
        success: 'Comment added to Jira ticket',
        error: 'Failed to add comment to Jira ticket',
      });

      return promise;
    },
  });
}

export async function fetchJiraTicket(
  axios: ReturnType<typeof useAxios>,
  account: string,
  ticketId: string
) {
  const response = await axios.post('/integration/jira/fetch', {
    account,
    ticketId,
  });
  return response.data;
}
